import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { Input, Button, ProTableOt } from "../../../ui/ot";

const mockSearchResults = [
  {
    name: "API_KEY",
    card: "KEY001",
    dob: "1990-01-01",
    category: "測試用群組",
    library: "介壽校區",
    address: "Address 1",
    phone: "123-456-7890",
  },
  {
    name: "Camel",
    card: "KEY002",
    dob: "2002-03-30",
    category: "測試用群組",
    library: "巨輪校區",
    address: "高雄市",
    phone: "097-037-7517",
  },
  {
    name: "FkDogg",
    card: "KEY003",
    dob: "2002-03-31",
    category: "測試用群組",
    library: "和平校區",
    address: "台南市",
    phone: "091-234-5123",
  },
];

const initialHoldsData = [
  {
    priority: '1',
    patron: 'Mr Ou John (9235)',
    notes: '',
    date: '07/18/2024',
    expiration: '07/25/2024',
    pickupLibrary: '介壽校區',
    details: 'Item waiting at 介壽校區 since 07/18/2024',
  },
  {
    priority: '2',
    patron: 'API_KEY (KEY001)',
    notes: '',
    date: '07/18/2024',
    expiration: '',
    pickupLibrary: '介壽校區',
    details: 'Next available',
  },
  {
    priority: '3',
    patron: 'API_KEY (KEY001)',
    notes: '',
    date: '07/19/2024',
    expiration: '',
    pickupLibrary: '介壽校區',
    details: 'Next available',
  }
];

const CirculationHolds = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState(mockSearchResults);
  const [results, setResults] = useState([]);
  const [holdsData, setHoldsData] = useState(initialHoldsData);
  const navigate = useNavigate();
  const location = useLocation();
  const { title } = location.state || { title: "Unknown" };

  useEffect(() => {
    if (searchTerm) {
      const filteredSuggestions = mockSearchResults.filter(
        (result) =>
          result.name.includes(searchTerm) || result.card.includes(searchTerm)
      );
      setSuggestions(filteredSuggestions);
    } else {
      setSuggestions([]);
    }
  }, [searchTerm]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.name);
    setSuggestions([]);
    setResults([suggestion]);
    navigate(`../CirculationPlace/${suggestion.name}`, { state: { title } });
  };

  const handleSearchClick = () => {
    const filteredResults = mockSearchResults.filter(
      (result) =>
        result.name.includes(searchTerm) || result.card.includes(searchTerm)
    );
    setResults(filteredResults);
    setSuggestions([]);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return; // already at the top
    const newData = [...holdsData];
    [newData[index - 1], newData[index]] = [newData[index], newData[index - 1]];
    setHoldsData(newData);
  };

  const handleMoveDown = (index) => {
    if (index === holdsData.length - 1) return; // already at the bottom
    const newData = [...holdsData];
    [newData[index], newData[index + 1]] = [newData[index + 1], newData[index]];
    setHoldsData(newData);
  };

  const handleDelete = (index) => {
    const newData = holdsData.filter((_, i) => i !== index);
    setHoldsData(newData);
  };

  const columns = [
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
    },
    {
      title: 'Change priority',
      dataIndex: 'changePriority',
      key: 'changePriority',
      render: (_, __, index) => (
        <UpDownButton>
          <button onClick={() => handleMoveUp(index)}>⬆</button>
          <button onClick={() => handleMoveDown(index)}>⬇</button>
        </UpDownButton>
      ),
    },
    {
      title: 'Patron',
      dataIndex: 'patron',
      key: 'patron',
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Expiration',
      dataIndex: 'expiration',
      key: 'expiration',
    },
    {
      title: 'Pickup library',
      dataIndex: 'pickupLibrary',
      key: 'pickupLibrary',
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
    },
    {
      title: 'Delete',
      dataIndex: 'delete',
      key: 'delete',
      render: (_, __, index) => (
        <DeleteButton onClick={() => handleDelete(index)}>❌</DeleteButton>
      ),
    },
  ];

  return (
    <MainContainer>
      <SearchContainer>
        <h2>Search patrons</h2>
        <SearchBox>
          <Label htmlFor="search-input">
            Enter patron card number or partial name:
          </Label>
          <Input
            id="search-input"
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <Button onClick={handleSearchClick}>Search</Button>
          {suggestions.length > 0 && (
            <SuggestionsBox>
              {suggestions.map((suggestion, index) => (
                <SuggestionItem
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion.name}, ({suggestion.card}){" "}
                  <Highlight>{suggestion.library}</Highlight>
                </SuggestionItem>
              ))}
            </SuggestionsBox>
          )}
        </SearchBox>
        <BrowseBox>
          <p>Browse by last name:</p>
          <AlphabetLinks>
            {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((letter) => (
              <a key={letter} href={`#${letter}`}>
                {letter}
              </a>
            ))}
          </AlphabetLinks>
        </BrowseBox>
      </SearchContainer>
      <WhiteBox>
        {results.length > 0 && (
          <ProTableOt
            columns={[
              { title: 'Name', dataIndex: 'name', key: 'name' },
              { title: 'Card', dataIndex: 'card', key: 'card' },
              { title: 'Date of birth', dataIndex: 'dob', key: 'dob' },
              { title: 'Category', dataIndex: 'category', key: 'category' },
              { title: 'Library', dataIndex: 'library', key: 'library' },
              { title: 'Address', dataIndex: 'address', key: 'address' },
              { title: 'Phone', dataIndex: 'phone', key: 'phone' },
            ]}
            dataSource={results}
            rowKey="card"
          />
        )}
      </WhiteBox>
      <WhiteBox>
        <ProTableOt
          columns={columns}
          dataSource={holdsData}
          rowKey="patron"
        />
      </WhiteBox>
    </MainContainer>
  );
};

const MainContainer = styled.div`
  font-family: Arial, sans-serif;
  text-align: left;
  padding: 20px;
`;

const SearchContainer = styled.div`
  background-color: #f8f8f8;
  border: 1px solid #ddd;
  padding: 20px;
  border-radius: 4px;

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const SearchBox = styled.div`
  margin-bottom: 20px;
  position: relative;

  @media (max-width: 768px) {
    margin-bottom: 10px;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: bold;

  @media (max-width: 768px) {
    margin-bottom: 5px;
  }
`;

const BrowseBox = styled.div`
  p {
    margin-bottom: 8px;
    font-weight: bold;

    @media (max-width: 768px) {
      margin-bottom: 5px;
    }
  }
`;

const AlphabetLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  a {
    margin: 4px;
    color: green;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }

    @media (max-width: 768px) {
      margin: 2px;
    }
  }
`;

const SuggestionsBox = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  border: 1px solid #ddd;
  border-top: none;
  position: absolute;
  width: calc(100% - 100px);
  max-height: 150px;
  overflow-y: auto;
  background-color: #fff;
  z-index: 1;

  @media (max-width: 768px) {
    width: calc(100% - 40px);
  }
`;

const SuggestionItem = styled.li`
  padding: 8px;
  cursor: pointer;
  &:hover {
    background-color: #f2f2f2;
  }

  @media (max-width: 768px) {
    padding: 5px;
  }
`;

const Highlight = styled.span`
  background-color: #d4edda;
  border-radius: 3px;
  padding: 0 4px;
`;

const WhiteBox = styled.div`
  background-color: #fff;
  padding: 1em;
  width: -webkit-fill-available;

  @media (max-width: 768px) {
    padding: 0.5em;
  }
`;

const UpDownButton = styled.div`
  display: flex;
  gap: 10px;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: red;
  cursor: pointer;
  font-size: 18px;
  padding: 0;
`;

export default CirculationHolds;
